import {toast} from '@wandb/components';
import {useCallback, useMemo, useRef, useState} from 'react';

import {
  useGrantPersonalSecretToWbAgentMutation,
  useRevokePersonalSecretFromWbAgentMutation,
  useWbAgentPersonalSecretsWithGrantsQuery,
} from '../../../generated/graphql';
import {
  extractErrorMessageFromApolloError,
  propagateErrorsContext,
} from '../../../util/errors';
import {AGENT_NAME} from '../constants';

/**
 * PersonalSecret is a secret the viewer holds, annotated with ARIA's access.
 */
export interface PersonalSecret {
  createdAt: string;
  granted: boolean;
  name: string;
  /** pending denotes whether a secret's grant or revoke is in flight. */
  pending: boolean;
  uuid: string;
}

export type PersonalSecretGrantsStatus = 'loading' | 'ready' | 'error';

/**
 * UsePersonalSecretGrantsResult is the secret list, its load state, and the
 * actions that change it.
 */
export interface UsePersonalSecretGrantsResult {
  errorMessage?: string;
  grantedCount: number;
  reload: () => void;
  secrets: PersonalSecret[];
  setGranted: (uuid: string, granted: boolean) => Promise<void>;
  status: PersonalSecretGrantsStatus;
}

interface OptimisticGrant {
  granted: boolean;
  operationID: number;
}

const EMPTY_OPTIMISTIC_GRANTS: ReadonlyMap<string, OptimisticGrant> = new Map();

/**
 * usePersonalSecretGrants owns which of the viewer's personal secrets ARIA may
 * use in one organization, and the writes that change it.
 */
export function usePersonalSecretGrants(
  organizationId?: string
): UsePersonalSecretGrantsResult {
  // Maps a uuid to the state the user asked for while its write is in flight,
  // so the switch moves on click.
  const [optimisticGrants, setOptimisticGrants] = useState<
    ReadonlyMap<string, OptimisticGrant>
  >(EMPTY_OPTIMISTIC_GRANTS);
  const nextOperationID = useRef(0);
  const writesBySecret = useRef(new Map<string, Promise<void>>());

  const {data, error, loading, refetch} =
    useWbAgentPersonalSecretsWithGrantsQuery({
      variables: {organizationId: organizationId ?? ''},
      skip: organizationId == null,
      fetchPolicy: 'cache-and-network',
      // Without this a refetch would render no frame of its own, so Retry would
      // look dead until the response lands.
      notifyOnNetworkStatusChange: true,
      // A read-back must not join a pre-mutation fetch.
      context: {...propagateErrorsContext(), queryDeduplication: false},
    });

  const [grantSecret] = useGrantPersonalSecretToWbAgentMutation({
    context: propagateErrorsContext(),
  });
  const [revokeSecret] = useRevokePersonalSecretFromWbAgentMutation({
    context: propagateErrorsContext(),
  });

  const grantedUuids = useMemo(
    () =>
      new Set(
        (data?.viewer?.wbAgentPersonalSecretGrants ?? []).map(
          grant => grant.secret.uuid
        )
      ),
    [data?.viewer?.wbAgentPersonalSecretGrants]
  );

  const secrets = useMemo(
    () =>
      (data?.viewer?.secrets ?? []).map(secret => ({
        uuid: secret.uuid,
        name: secret.name,
        createdAt: secret.createdAt,
        granted:
          optimisticGrants.get(secret.uuid)?.granted ??
          grantedUuids.has(secret.uuid),
        pending: optimisticGrants.has(secret.uuid),
      })),
    [data?.viewer?.secrets, grantedUuids, optimisticGrants]
  );

  const reload = useCallback(() => {
    // NOTE: The query hook already reports the error through `status`, so the
    // catch is only here to avoid an unhandled rejection.
    void refetch().catch(() => undefined);
  }, [refetch]);

  const setGranted = useCallback(
    async (uuid: string, granted: boolean) => {
      const operationID = ++nextOperationID.current;
      setOptimisticGrants(current =>
        new Map(current).set(uuid, {granted, operationID})
      );

      const writeSecret = async () => {
        try {
          if (granted) {
            await grantSecret({variables: {secretUuid: uuid}});
          } else {
            await revokeSecret({variables: {secretUuid: uuid}});
          }

          // NOTE: Grant returns one grant and revoke returns `success`, so
          // neither payload says what `wbAgentPersonalSecretGrants` now holds.
          // Refetch for the authoritative list.
          await refetch().catch(() => undefined);
        } catch (err) {
          toast.error(
            extractErrorMessageFromApolloError(err) ??
              (granted
                ? `Failed to share that secret with ${AGENT_NAME}.`
                : 'Failed to stop sharing that secret.')
          );
        }
        setOptimisticGrants(current =>
          current.get(uuid)?.operationID === operationID
            ? withoutKey(current, uuid)
            : current
        );
      };
      const previous = writesBySecret.current.get(uuid) ?? Promise.resolve();
      const write = previous.then(writeSecret, writeSecret);
      writesBySecret.current.set(uuid, write);
      const removeWrite = () => {
        if (writesBySecret.current.get(uuid) === write) {
          writesBySecret.current.delete(uuid);
        }
      };
      void write.then(removeWrite, removeWrite);

      await write;
    },
    [grantSecret, refetch, revokeSecret]
  );

  return useMemo<UsePersonalSecretGrantsResult>(
    () => ({
      secrets,
      grantedCount: secrets.filter(secret => secret.granted).length,
      status: error
        ? 'error'
        : isInitialLoad(loading, data)
          ? 'loading'
          : 'ready',
      errorMessage: extractErrorMessageFromApolloError(error),
      reload,
      setGranted,
    }),
    [data, error, loading, reload, secrets, setGranted]
  );
}

/**
 * isInitialLoad separates the first read from a background revalidation, so a
 * `cache-and-network` refetch does not put a populated panel back into loading.
 */
function isInitialLoad(loading: boolean, data: unknown): boolean {
  return loading && data == null;
}

function withoutKey(
  map: ReadonlyMap<string, OptimisticGrant>,
  uuid: string
): ReadonlyMap<string, OptimisticGrant> {
  const next = new Map(map);
  next.delete(uuid);
  return next;
}
